import { useEffect, useMemo } from "react";

import {
  BUSINESS_PREFETCH_RULES,
  IDLE_TIMEOUT_MS,
  type PrefetchEntry,
  type RouteImporter,
} from "@/app/constants/routePrefetch.constants";
import type { TSidebarItem } from "@/app/constants/routes_sidebar/sidebarNav.types";
import { Permissions } from "@/interfaces/globals";
import { useBusinessSidebarData } from "@/pages/SidebarLinks";
import type { UserPermission } from "@/redux/slices/permissionSlice";
import { useAppSelector, type RootState } from "@/redux/store";
import { getWorkspace } from "@/utils/storage/appSession";

type PrefetchGuard = {
  cancelled: boolean;
};

type BrowserNetworkInformation = {
  saveData?: boolean;
  effectiveType?: string;
};

const MAX_ROUTE_PREFETCH_IMPORTS = 6;
const SLOW_NETWORK_TYPES = new Set(["slow-2g", "2g"]);

const getBusinessSubdomain = (): string => {
  return window.location.hostname.split(".")?.[0] ?? "";
};

const canRunRoutePrefetch = (): boolean => {
  const networkInformation = (
    navigator as Navigator & {
      connection?: BrowserNetworkInformation;
    }
  ).connection;

  if (!networkInformation) {
    return true;
  }

  if (networkInformation.saveData) {
    return false;
  }

  if (
    networkInformation.effectiveType &&
    SLOW_NETWORK_TYPES.has(networkInformation.effectiveType)
  ) {
    return false;
  }

  return true;
};

const hasWorkspaceReadPermission = (
  requiredPermissions: Permissions[],
  grantedPermissions: UserPermission[],
): boolean => {
  if (requiredPermissions.length === 0) {
    return true;
  }

  if (grantedPermissions.length === 0) {
    return false;
  }

  const currentWorkspace = getWorkspace();

  const hasAllReadPermission = grantedPermissions.some((permissionEntry) => {
    return (
      permissionEntry.resource === Permissions.ALL &&
      permissionEntry.action === "READ" &&
      (permissionEntry.workspace === "*" ||
        permissionEntry.workspace === currentWorkspace)
    );
  });

  if (hasAllReadPermission) {
    return true;
  }

  return requiredPermissions.some((requiredPermission) => {
    return grantedPermissions.some((permissionEntry) => {
      return (
        permissionEntry.resource === requiredPermission &&
        permissionEntry.action === "READ" &&
        (permissionEntry.workspace === "*" ||
          permissionEntry.workspace === currentWorkspace)
      );
    });
  });
};

const collectVisibleBusinessPaths = (
  sidebarItems: TSidebarItem[],
  grantedPermissions: UserPermission[],
): string[] => {
  const visiblePaths = new Set<string>();

  for (const sidebarItem of sidebarItems) {
    if (!sidebarItem.value) {
      continue;
    }

    const hasParentPermission = hasWorkspaceReadPermission(
      sidebarItem.permission,
      grantedPermissions,
    );
    if (!hasParentPermission) {
      continue;
    }

    if (!sidebarItem.hasChild || !sidebarItem.children?.length) {
      visiblePaths.add(sidebarItem.value);
      continue;
    }

    for (const childItem of sidebarItem.children) {
      if (!childItem.value) {
        continue;
      }

      const hasChildPermission = hasWorkspaceReadPermission(
        childItem.permission,
        grantedPermissions,
      );
      if (!hasChildPermission) {
        continue;
      }

      visiblePaths.add(`${sidebarItem.value}${childItem.value}`);
    }
  }

  return Array.from(visiblePaths);
};

const getBusinessPrefetchQueue = (visiblePaths: string[]): RouteImporter[] => {
  const prefetchEntries = new Map<string, PrefetchEntry>();

  for (const pathname of visiblePaths) {
    const matchingRule = BUSINESS_PREFETCH_RULES.find((rule) => {
      return rule.matches(pathname);
    });

    if (matchingRule) {
      prefetchEntries.set(matchingRule.entry.key, matchingRule.entry);
    }
  }

  return Array.from(prefetchEntries.values()).map((entry) => {
    return entry.importModule;
  });
};

/**
 * Sequentially imports each module in the prefetch queue, stopping early when
 * the guard is cancelled. Import errors are swallowed so a failed prefetch
 * never surfaces to the user.
 *
 * @param {RouteImporter[]} prefetchQueue - Ordered importers to execute.
 * @param {PrefetchGuard} guard - Cancellation guard shared with cleanup.
 * @returns {Promise<void>}
 */
const runPrefetchQueue = async (
  prefetchQueue: RouteImporter[],
  guard: PrefetchGuard,
): Promise<void> => {
  for (const importModule of prefetchQueue) {
    if (guard.cancelled) {
      break;
    }

    try {
      await importModule();
    } catch {
      // Prefetch failures must never surface to the user.
    }
  }
};

/**
 * Prefetches business route chunks that are actually visible for the current
 * user, based on sidebar permissions. This avoids warming inaccessible routes.
 *
 * Uses requestIdleCallback with a setTimeout fallback for Safari. Modules are
 * imported sequentially to avoid saturating the network.
 *
 * @returns {void}
 */
export const useRoutePrefetch = (): void => {
  const businessSidebarData = useBusinessSidebarData();
  const grantedPermissions = useAppSelector(
    (state: RootState): UserPermission[] => state.permissions.data,
  );

  const prefetchQueue = useMemo((): RouteImporter[] => {
    if (getBusinessSubdomain() !== "business") {
      return [];
    }

    const visibleBusinessPaths = collectVisibleBusinessPaths(
      businessSidebarData,
      grantedPermissions,
    );

    return getBusinessPrefetchQueue(visibleBusinessPaths).slice(
      0,
      MAX_ROUTE_PREFETCH_IMPORTS,
    );
  }, [businessSidebarData, grantedPermissions]);

  useEffect((): (() => void) => {
    const guard: PrefetchGuard = { cancelled: false };
    let idleCallbackId: number | undefined;
    let timerId: ReturnType<typeof setTimeout> | undefined;

    if (prefetchQueue.length === 0 || !canRunRoutePrefetch()) {
      return (): void => {
        guard.cancelled = true;
      };
    }

    if ("requestIdleCallback" in window) {
      idleCallbackId = requestIdleCallback(
        () => void runPrefetchQueue(prefetchQueue, guard),
        { timeout: IDLE_TIMEOUT_MS },
      );
    } else {
      timerId = setTimeout(
        () => void runPrefetchQueue(prefetchQueue, guard),
        IDLE_TIMEOUT_MS,
      );
    }

    return (): void => {
      guard.cancelled = true;

      if (idleCallbackId !== undefined) {
        cancelIdleCallback(idleCallbackId);
      }

      if (timerId !== undefined) {
        clearTimeout(timerId);
      }
    };
  }, [prefetchQueue]);
};
